import React, { useEffect, useMemo, useState } from "react"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css" // Import the "snow" theme styles
import "./App.css"
// React Router
import { useParams, useNavigate } from "react-router-dom"

// MUI
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material"

import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers"

// Form
import { useForm, Controller } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from "yup"

// Utils
import dayjs, { Dayjs } from "dayjs"
import { useTranslation } from "react-i18next"
import { newAdapter } from "../../../utils"

//Type
import { Role, TokenPayload, User } from "../../../@types"
import jwtDecode from "jwt-decode"

import {
  addPDPAVersion,
  updatePDPAVersion,
  getPDPAVersionById,
  getPDPAByStatus,
} from "../../../services/PDPAService"
import Swal from "sweetalert2"

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  editId?: string
}

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    [{ size: ["small", false, "large", "huge"] }],
    ["clean"],
  ],
}

const englishOnlyRegex = /^[A-Za-z0-9_.-]+$/

const CreatePDPAEdit: React.FC<MyEditorProps> = ({ type = "add", editId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id = "" } = useParams()
  const token = localStorage.getItem("access_token") || ""
  const decoded: TokenPayload = jwtDecode(token)
  const Username = decoded?.UserId ? decoded.Username : ""

  const [pdpaData, setPdpaData] = useState({
    version: "",
    pdpaName: "",
    activeDate: "",
    expirationDate: "",
    is_active: true,
    thaiContent: "",
    englishContent: "",
  })

  const formValidationSchema = yup.object().shape({
    version: yup
      .string()
      .required("Version is required")
      .matches(
        englishOnlyRegex,
        "Please use only English letters, numbers, and the characters _ . - Spaces are not allowed."
      ),
    pdpaName: yup.string().required("PDPA Name is required"),
    activeDate: yup.mixed().nullable().required("Active Date is required"),
    expirationDate: yup
      .mixed()
      .nullable()
      .required("Expiration Date is required"),
    is_active: yup.boolean(),
    thaiContent: yup.string().required("Thai content is required"),
    englishContent: yup.string().required("English content is required"),
  })

  const {
    register,
    // setValue,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(formValidationSchema),
  })

  const thaiContent = watch("thaiContent")
  const englishContent = watch("englishContent")

  useEffect(() => {
    const fetchData = async () => {
      try {
        const respData = await getPDPAVersionById(id)
        if (respData.status === 200) {
          setPdpaData({
            version: respData?.data?.data?.version || "",
            pdpaName: respData?.data?.data?.pdpaName || "",
            activeDate: respData?.data?.data?.activeDate || null,
            expirationDate: respData?.data?.data?.expirationDate || null,
            is_active: respData?.data?.data?.is_active || false,
            thaiContent: respData?.data?.data?.thaiContent || "",
            englishContent: respData?.data?.data?.englishContent || "",
          })
          reset({
            version: respData?.data?.data?.version || "",
            pdpaName: respData?.data?.data?.pdpaName || "",
            activeDate: respData?.data?.data?.activeDate || null,
            expirationDate: respData?.data?.data?.expirationDate || null,
            is_active: respData?.data?.data?.is_active || false,
            thaiContent: respData?.data?.data?.thaiContent || "",
            englishContent: respData?.data?.data?.englishContent || "",
          })
        }
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }
    if (type === "edit" && typeof id === "string" && id.length > 0) {
      fetchData()
    }
  }, [id, type])

  const postPDPAVersion = async (type: string, reqData: any) => {
    try {
      let respData
      if (type === "add") {
        respData = await addPDPAVersion(reqData)
      } else if (type === "edit" && typeof id === "string" && id.length > 0) {
        respData = await updatePDPAVersion(id, reqData)
      }
      if (respData && respData?.status === 200) {
        Swal.fire({
          icon: "success",
          title: `${t("Success")}`,
          text: "บันทึกPDPAสำเร็จ",
          confirmButtonText: `${t("ok")}`,
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = "/admin/pdpa/upload"
            return
          }
        })
      } else {
        Swal.fire({
          icon: "error",
          title: `${t("Error")}`,
          text: `${t("Please try again")}`,
          confirmButtonText: `${t("ok")}`,
        })
      }
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }

  const submitForm = async (data: any) => {
    const pdpaStatus = await getPDPAByStatus("active")
    const reqData = {
      version: data.version || "",
      pdpaName: data.pdpaName || "",
      activeDate: data.activeDate || null,
      expirationDate: data.expirationDate || null,
      is_active: data.is_active || false,
      thaiContent: data.thaiContent || "",
      englishContent: data.englishContent || "",
    } as any

    if (type === "edit") {
      reqData.modified_by = Username || ""
    } else {
      reqData.created_by = Username || ""
    }
    const checkPDPA =
      type === "edit" &&
      pdpaStatus?.data?.pdpas?.count > 0 &&
      pdpaStatus?.data?.pdpas?.data?.id !== id
        ? true
        : type === "add" && pdpaStatus?.data?.pdpas?.count > 0
        ? true
        : false

    if (
      pdpaStatus?.status === 200 &&
      data?.is_active === true &&
      // pdpaStatus?.data?.pdpas?.count > 0
      checkPDPA
      //Check count pdpaStatus active
    ) {
      Swal.fire({
        icon: "question",
        title: `${t("Success")}`,
        text: `${t("PDPA.text_alert_confrim_edit")}`,
        showCancelButton: true,
        confirmButtonText: `${t("Confirm")}`,
        cancelButtonText: `${t("Cancel")}`,
      }).then((result) => {
        if (result.isConfirmed) {
          postPDPAVersion(type, reqData)
        }
      })
    } else {
      //case create PDPA
      postPDPAVersion(type, reqData)
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1400px",
        margin: "0 auto",
        padding: {
          xs: 2,
          sm: 3,
          md: 4,
        },
      }}
    >
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: {
              xs: "1.5rem",
              md: "2rem",
            },
          }}
        >
          {type === "edit" ? "Edit PDPA Version" : "Create New PDPA Version"}
        </Typography>

        {type === "edit" && editId && (
          <Typography variant="body1">Edit ID : {editId}</Typography>
        )}
      </Box>

      <form onSubmit={handleSubmit(submitForm)}>
        <Paper
          elevation={3}
          sx={{
            p: {
              xs: 2,
              md: 4,
            },
            borderRadius: 3,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Version"
                placeholder="Example : v1_0-0"
                InputLabelProps={{ shrink: true }}
                {...register("version")}
                // value={pdpaData?.version || ""}
                // onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                //   setPdpaData((prevData) => ({
                //     ...prevData,
                //     version: event.target.value,
                //   }))
                //   errors.version = undefined
                // }}
                error={!!errors.version}
              />

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {/* {errors?.version?.message} */}
              </Typography>

              <FormHelperText>
                {t("PDPA.text_alert_error_version")}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PDPA Name"
                placeholder="Example : PDPA_MAIN"
                InputLabelProps={{ shrink: true }}
                {...register("pdpaName")}
                // value={pdpaData?.pdpaName || ""}
                // onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                //   setPdpaData((prevData) => ({
                //     ...prevData,
                //     pdpaName: event.target.value,
                //   }))
                //   errors.pdpaName = undefined
                // }}
                error={!!errors.pdpaName}
              />

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {/* {errors.pdpaName?.message} */}
              </Typography>

              <FormHelperText>
                {t("PDPA.text_alert_error_version")}
              </FormHelperText>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={newAdapter} adapterLocale="th">
                <Controller
                  // defaultValue={pdpaData.activeDate || ""}
                  control={control}
                  name="activeDate"
                  render={({ field }) => (
                    <MobileDatePicker
                      label="Active Date"
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : ""}
                      onChange={(newValue) => {
                        field.onChange(
                          newValue ? dayjs(newValue).toISOString() : null
                        )
                      }}
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </LocalizationProvider>

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {errors.activeDate?.message as string}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={newAdapter} adapterLocale="th">
                <Controller
                  control={control}
                  name="expirationDate"
                  render={({ field }) => (
                    <MobileDatePicker
                      label="Expiration Date"
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : ""}
                      onChange={(newValue) => {
                        field.onChange(
                          newValue ? dayjs(newValue).toISOString() : null
                        )
                      }}
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              </LocalizationProvider>

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {errors.expirationDate?.message as string}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Controller
                control={control}
                name="is_active"
                render={({ field }) => (
                  <FormControlLabel
                    label="Active"
                    control={
                      <Checkbox
                        checked={field.value || false}
                        onChange={(newValue) => {
                          field.onChange(newValue ? newValue : false)
                        }}
                      />
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                ภาษาไทย
              </Typography>

              <Controller
                control={control}
                name="thaiContent"
                render={({ field }) => (
                  <Box
                    sx={{
                      ".ql-container": {
                        minHeight: "350px",
                        maxHeight: "500px",
                        overflowY: "auto",
                        fontSize: "16px",
                      },
                      ".ql-editor": {
                        minHeight: "350px",
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      // value={pdpaData?.thaiContent}
                      value={field.value}
                      modules={modules}
                      onChange={field.onChange}
                      placeholder="กรอกข้อมูลภาษาไทย..."
                    />
                  </Box>
                )}
              />

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {/* {errors.thaiContent?.message} */}
              </Typography>
            </Grid>

            {/* ROW 5 */}
            {/* English Editor */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                }}
              >
                English
              </Typography>

              <Controller
                control={control}
                name="englishContent"
                render={({ field }) => (
                  <Box
                    sx={{
                      ".ql-container": {
                        minHeight: "350px",
                        maxHeight: "500px",
                        overflowY: "auto",
                        fontSize: "16px",
                      },
                      ".ql-editor": {
                        minHeight: "350px",
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      // value={pdpaData?.englishContent}
                      value={field.value}
                      modules={modules}
                      onChange={field.onChange}
                      placeholder="Input English content..."
                    />
                  </Box>
                )}
              />

              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "block" }}
              >
                {/* {errors.englishContent?.message} */}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Preview ภาษาไทย
                </Typography>

                <div
                  dangerouslySetInnerHTML={{
                    __html: thaiContent || "",
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 700,
                  }}
                >
                  Preview English
                </Typography>

                <div
                  dangerouslySetInnerHTML={{
                    __html: englishContent || "",
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "center",
                    md: "flex-end",
                  },
                  gap: 2,
                  mt: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate(-1)}
                  sx={{
                    minWidth: {
                      xs: "100%",
                      sm: "160px",
                    },
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    minWidth: {
                      xs: "100%",
                      sm: "160px",
                    },
                  }}
                >
                  {type === "edit" ? "Update" : "Create"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </form>
    </Box>
  )
}

export default CreatePDPAEdit
